import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useState } from "react";
import { Link } from "react-router";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { formatPercent } from "_shared/percentages/percent";
import LoadingButton from "_shared/_views/loading/loading-button";
import Loader from "_shared/_views/loading/loader";
import { FIRM_ONBOARDING_DEFAULTS_LABEL } from "legal-entities/vendor-rollouts/firm-onboarding-routes";
import "permissions/__extensions/users/user-extensions";
import useSessionUser from "permissions/session/use-session-user";
import {
  isUnauthorizedClientProposalTemplateError,
  useGetClientProposalTemplateQuery,
  useUpdateClientProposalTemplateMutation,
} from "work/client-proposal-templates/_api/client-proposal-template-api";
import type { CopiedProposalTemplateJSON } from "work/client-proposal-templates/_schema/client-proposal-template-schema";
import {
  hasUnsavedChanges,
  toFormState,
  toUpdateRequest,
  type ClientProposalTemplateFormState,
} from "work/client-proposal-templates/_views/client-proposal-template-form";
import { useGetProposalTemplatesQuery } from "work/proposal-templates/_api/proposal-template-api";
import ProposalTemplate, { ProposalTemplateParty } from "work/proposal-templates/proposal-template";

// #region Styles
const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
}));
const Introduction = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(2),
  maxWidth: "48rem",
}));
const Fields = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  maxWidth: "48rem",
}));
const SectionHeading = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));
const StatusAlert = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  maxWidth: "48rem",
}));
const CopySummary = styled("dl")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  columnGap: theme.spacing(1.5),
  rowGap: theme.spacing(0.5),
  margin: 0,
}));
const CopyLabel = styled("dt")(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: theme.typography.body2.fontSize,
}));
const CopyValue = styled("dd")(({ theme }) => ({
  margin: 0,
  fontSize: theme.typography.body2.fontSize,
  overflowWrap: "anywhere",
}));
const Actions = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  marginTop: theme.spacing(3),
}));
// #endregion

/**
 * Where a reader goes to make a proposal template to pick here.
 *
 * The Proposals page's Templates tab for now, which lists them and says how to save one. Point it
 * at the template creation view once that lands.
 */
export const PROPOSAL_TEMPLATE_CREATION_PATH = "/proposals/templates";

/**
 * The Firm Onboarding page's Defaults tab (#6261): the saved proposal template a draft is built
 * from when a firm this organization invited finishes creating its account.
 *
 * The client's own half of automatic proposal creation (issue #5214). The defaults hold their own
 * copy of the template, taken when it is picked and saved, so a later edit to the saved template —
 * or its deletion — does not change what firms are offered until somebody picks it again.
 *
 * **There is no id on this page, and no organization picker.** The API resolves the record from the
 * bearer token, so the page configures the organization the reader signed in as and can reach no
 * other — see `client-proposal-template-api.ts`.
 *
 * The templates offered are the reader's client-side ones whatever side the session is viewing
 * from: these defaults build the client's half of a proposal, and the server refuses a vendor one.
 */
export default function FirmOnboardingDefaults() {
  const user = useSessionUser();

  const configurationQuery = useGetClientProposalTemplateQuery();
  const [updateConfiguration, updateState] = useUpdateClientProposalTemplateMutation();
  const configuration = configurationQuery.data;
  const templatesQuery = useGetProposalTemplatesQuery({});

  /**
   * The edit in hand, or null while the page is showing exactly what the API returned.
   *
   * Null rather than a copy seeded in an effect. A `useEffect` that pushed the loaded configuration
   * into state would be a `set-state-in-effect` bailout, and it would also race a refetch: the
   * saved record arriving second would be written over the reader's choice. Derived below instead,
   * and put back to null on a successful save so the fresh record becomes what is shown.
   */
  const [edit, setEdit] = useState<ClientProposalTemplateFormState | null>(null);
  const state = edit ?? toFormState(configuration);

  const templates = ProposalTemplate.fromJSONArray(templatesQuery.data).filter(
    (template) => template.party === ProposalTemplateParty.Client,
  );
  const picked = templates.find((template) => template.id.value === state.copyFromProposalTemplateId) ?? null;
  const copy = configuration?.proposalTemplate ?? undefined;

  const isDirty = hasUnsavedChanges(state, configuration);
  const canEdit = user?.canConfigureClientProposalTemplates() === true;
  const isBusy = updateState.isLoading || configurationQuery.isFetching;

  function change(fields: Partial<ClientProposalTemplateFormState>) {
    setEdit({ ...state, ...fields });
  }

  async function handleSave() {
    const saved = await saveFirmOnboardingDefaults(updateConfiguration, toUpdateRequest(state));
    if (saved) setEdit(null);
  }

  if (!user || configurationQuery.isLoading) return <Loader text="Loading firm onboarding defaults..." />;

  if (configurationQuery.isError) {
    return (
      <StatusAlert severity="error">
        {isUnauthorizedClientProposalTemplateError(configurationQuery.error)
          ? "You do not have permission to see your organization's firm onboarding defaults."
          : (getErrorMessageFromUnknown(configurationQuery.error) ??
            "Your organization's firm onboarding defaults could not be loaded.")}
      </StatusAlert>
    );
  }

  return (
    <>
      <SectionTitle variant="h6">{FIRM_ONBOARDING_DEFAULTS_LABEL}</SectionTitle>
      <Introduction variant="body2">
        When a firm you have invited creates its account, Attorney Hub can build that firm&apos;s
        proposal from the proposal template you choose here. It stops at a draft: you still open it,
        staff the hourly categories and press send.
      </Introduction>

      {configuration?.gapReason && (
        // The server's sentence rather than a copy of it, and in the words the roll-out records
        // against the firm — so the board and this page cannot explain the same stall differently.
        <StatusAlert severity="info">
          <AlertTitle>No drafts are being built</AlertTitle>
          {configuration.gapReason}
        </StatusAlert>
      )}
      {configuration?.isReadyForAutoCreation && (
        <StatusAlert severity="success">
          Drafts are being built for firms that finish creating their accounts.
        </StatusAlert>
      )}
      {!canEdit && (
        <StatusAlert severity="warning">
          You can see these defaults but not change them. Setting them needs entity-wide access to
          proposals and to discounts.
        </StatusAlert>
      )}

      <Fields>
        <SectionHeading variant="subtitle1">Proposal template</SectionHeading>
        <Autocomplete
          options={templates}
          value={picked}
          loading={templatesQuery.isFetching}
          disabled={!canEdit || isBusy}
          getOptionLabel={(template) => template.name}
          getOptionKey={(template) => template.id.value}
          isOptionEqualToValue={(option, value) => option.id.isEqualTo(value.id)}
          noOptionsText="You have no saved proposal templates yet."
          onChange={(_event, template) => change({ copyFromProposalTemplateId: template?.id.value ?? null })}
          renderInput={(params) => (
            <TextField
              {...params}
              label={copy ? "Replace with a proposal template" : "Proposal template"}
              helperText={
                "These defaults keep their own copy of the template as it is when you save, so later " +
                "edits to the template do not change them until you pick it again."
              }
            />
          )}
        />
        {picked && (
          <Alert severity="info">
            {copy
              ? `Saving replaces the current copy with ${picked.name} as it is now.`
              : `Saving copies ${picked.name} as it is now.`}
          </Alert>
        )}
        {copy ? (
          <CopiedTemplateSummary copy={copy} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No proposal template has been chosen yet.
          </Typography>
        )}
        <Button component={Link} to={PROPOSAL_TEMPLATE_CREATION_PATH} sx={{ alignSelf: "flex-start" }}>
          Go to Proposal Templates
        </Button>

        <Divider />
        <SectionHeading variant="subtitle1">Automatic creation</SectionHeading>
        <FormControlLabel
          control={
            <Switch
              checked={state.autoCreateEnabled}
              disabled={!canEdit || isBusy}
              onChange={(event) => change({ autoCreateEnabled: event.target.checked })}
            />
          }
          label="Build a draft proposal for each firm that creates its account"
        />
      </Fields>

      <Actions>
        <LoadingButton
          variant="contained"
          loading={updateState.isLoading}
          disabled={!canEdit || !isDirty}
          onClick={() => void handleSave()}
        >
          Save
        </LoadingButton>
      </Actions>
    </>
  );
}

/** What the copy the defaults hold says, in a few lines rather than the whole template. */
function CopiedTemplateSummary(props: Readonly<{ copy: CopiedProposalTemplateJSON }>) {
  const { copy } = props;
  return (
    <div>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Copied from <strong>{copy.sourceName}</strong> on {new Date(copy.copiedDate).toLocaleDateString()}
      </Typography>
      <CopySummary aria-label="The copy these defaults hold">
        <CopyLabel>Proposal name</CopyLabel>
        <CopyValue>{copy.proposalName || "—"}</CopyValue>
        <CopyLabel>Fee categories</CopyLabel>
        <CopyValue>{copy.feeCategoryCount}</CopyValue>
        <CopyLabel>Team</CopyLabel>
        <CopyValue>{describeTeam(copy)}</CopyValue>
        <CopyLabel>Discount</CopyLabel>
        <CopyValue>{formatPercent(copy.discount)}</CopyValue>
        <CopyLabel>Negotiable</CopyLabel>
        <CopyValue>{copy.negotiable ? "Yes" : "No"}</CopyValue>
        <CopyLabel>Conflicts check waived</CopyLabel>
        <CopyValue>{copy.conflictsCheckWaived ? "Yes" : "No"}</CopyValue>
        <CopyLabel>Documents</CopyLabel>
        <CopyValue>
          {countOf(copy.conflictsDocumentCount, "conflicts document")},{" "}
          {countOf(copy.policyDocumentCount, "policy document")}
        </CopyValue>
      </CopySummary>
    </div>
  );
}

/** The team as counts: the copy carries the leader's id, and no name to show for it. */
export function describeTeam(copy: Pick<CopiedProposalTemplateJSON, "teamLeaderUserId" | "teamMemberCount">): string {
  const members = countOf(copy.teamMemberCount, "member");
  if (copy.teamLeaderUserId) return copy.teamMemberCount > 0 ? `A leader and ${members}` : "A leader";
  return copy.teamMemberCount > 0 ? members : "No team";
}

function countOf(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

/**
 * Sends one save and reports the record it stored, or undefined.
 *
 * At module scope because it holds a `try`/`catch` around an `await` — a component containing one
 * is emitted uncompiled in its entirety, memoization and all (see app/CLAUDE.md).
 */
async function saveFirmOnboardingDefaults(
  update: ReturnType<typeof useUpdateClientProposalTemplateMutation>[0],
  request: ReturnType<typeof toUpdateRequest>,
) {
  try {
    const saved = await update(request).unwrap();
    enqueueSnackbar("Firm onboarding defaults saved", { variant: "success" });
    return saved;
  } catch (error) {
    console.error("Failed to save the firm onboarding defaults", error);
    enqueueSnackbar(
      isUnauthorizedClientProposalTemplateError(error)
        ? "You do not have permission to change your organization's firm onboarding defaults, or to use that proposal template."
        : (getErrorMessageFromUnknown(error) ?? "Failed to save the firm onboarding defaults. Please try again."),
      { variant: "error" },
    );
    return undefined;
  }
}
